// AdminDlqInspector.tsx — Phase 11E settlement DLQ inspector.
//
// Lists every entry in pending_settlements_dead_letter that has not yet
// been resolved. Each row shows match_id, attempts, last error, and
// offers two actions:
//   - Retry      → re-enqueues the job (same match_id) into pending_settlements
//   - Resolve    → mark the DLQ row resolved with an admin note
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens.
// The rose-tinted alert cards and emerald empty state stay (status
// colour-coding) but switch to ring-1 ring-inset semantic rings. All
// state + API calls unchanged.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
// Phase 19 / Batch 2 (#24): retry was a single-click action that
// re-enqueues a failed settlement. Add a confirm modal so misclicks
// don't double-fire the worker.
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';

interface DlqRow {
  id: number; original_id: number; match_id: string; game_id: string;
  attempts: number; last_error: string | null;
  enqueued_at: string; failed_at: string;
}

export default function DlqPanel() {
  const [rows, setRows] = useState<DlqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [resolveTarget, setResolveTarget] = useState<DlqRow | null>(null);
  // Batch 2 #24: target row for the Retry confirm modal.
  const [retryTarget, setRetryTarget] = useState<DlqRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<{ rows: DlqRow[] }>('/admin/settlement/dlq');
      setRows(r.rows);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const retry = async (id: number) => {
    setBusyId(id); setErr(null);
    try {
      await adminApi.post(`/admin/settlement/dlq/${id}/retry`, {});
      setToast('Re-enqueued — settlement worker will pick it up shortly.');
      await load();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  };

  const resolve = async (id: number, note: string) => {
    setBusyId(id); setErr(null);
    try {
      await adminApi.post(`/admin/settlement/dlq/${id}/resolve`, { note });
      setToast('Marked resolved.');
      setResolveTarget(null);
      await load();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4">
      {/* Phase 7 — one Settlement area: hop to the replay twin. */}
      <Link
        to="/settings/replay"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold
                   text-arena-600 dark:text-neon-200/70
                   bg-arena-100/70 dark:bg-white/[0.06]
                   ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                   hover:bg-arena-100 dark:hover:bg-white/10 transition"
      >
        <Icon name="swap_horiz" size={14} />
        <span>Also in Settlement: <span className="font-bold">re-run a game (Replay)</span></span>
      </Link>
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}
        {toast && <Banner tone="success">{toast}</Banner>}

        <motion.div
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                     backdrop-blur-md
                     bg-white/70 dark:bg-arena-900/60
                     flex flex-wrap gap-2 items-center"
        >
          <div className="text-[12px] text-arena-700 dark:text-neon-200/75 px-1 max-w-2xl">
            Settlement jobs that exhausted their retry budget. Investigate the error,
            fix root cause, then either Retry (re-enqueues) or Resolve (mark handled).
          </div>
          <div className="flex-1" />
          <button onClick={load}
                  className="grid place-items-center w-9 h-9 rounded-xl
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             text-arena-700 dark:text-neon-200/80
                             hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="refresh" size={16} />
          </button>
        </motion.div>

        {loading && rows.length === 0 && (
          <div className="text-center py-12 text-sm text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-2 align-middle" /> Loading…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl px-6 py-12 text-center
                       bg-gradient-to-br from-emerald-50 to-teal-50
                       dark:from-emerald-500/10 dark:to-teal-500/10
                       ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-400/30
                       shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                       backdrop-blur"
          >
            <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl
                            bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200
                            dark:bg-emerald-400/20 dark:text-emerald-200 dark:ring-emerald-400/30 mb-3">
              <Icon name="check_circle" size={28} filled />
            </div>
            <div className="font-bold text-emerald-900 dark:text-emerald-100">DLQ is empty</div>
            <div className="text-[12px] text-emerald-700 dark:text-emerald-300/80 mt-1">
              No failed settlement jobs waiting on you. Everything is settling cleanly.
            </div>
          </motion.div>
        )}

        {rows.length > 0 && (
          <div className="space-y-3">
            {rows.map((r, i) => (
              <motion.div key={r.id}
                          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.03 * i }}
                          className="rounded-2xl
                                     ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                                     bg-gradient-to-br from-rose-50/80 to-white/70
                                     dark:from-rose-500/[0.08] dark:to-arena-700/40
                                     backdrop-blur
                                     shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                                     p-4">
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                                   bg-rose-100 text-rose-700 ring-1 ring-rose-200
                                   dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30">
                    <Icon name="warning" size={20} filled />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-2 mb-1.5">
                      <span className="font-mono text-[13px] font-bold text-arena-900 dark:text-white">{r.match_id}</span>
                      <span className="text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded
                                       bg-arena-100/60 text-arena-700
                                       dark:bg-arena-700/50 dark:text-neon-200/70 capitalize">
                        {r.game_id}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold
                                       bg-rose-100 text-rose-700
                                       dark:bg-rose-400/15 dark:text-rose-300">
                        <Icon name="replay" size={11} /> {r.attempts} attempt{r.attempts === 1 ? '' : 's'}
                      </span>
                      <span className="ml-auto text-[10.5px] text-arena-600 dark:text-neon-200/50">
                        failed {new Date(r.failed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    </div>

                    {r.last_error && (
                      <div className="text-[11.5px] font-mono rounded-lg px-2.5 py-2 mb-3
                                      bg-white/80 dark:bg-arena-900/60
                                      ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30
                                      text-rose-900 dark:text-rose-200 break-all whitespace-pre-wrap">
                        {r.last_error}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => setRetryTarget(r)} disabled={busyId === r.id}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                         bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                                         text-white text-[12px] font-bold
                                         shadow-[0_2px_10px_rgba(16,185,129,0.35)]
                                         transition active:scale-95 disabled:opacity-50">
                        {busyId === r.id ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="restart_alt" size={13} />}
                        Retry now
                      </button>
                      <button onClick={() => setResolveTarget(r)} disabled={busyId === r.id}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                                         bg-white text-arena-800 ring-1 ring-inset ring-arena-200/60
                                         hover:bg-arena-50
                                         dark:bg-arena-700/40 dark:text-neon-200/90 dark:ring-arena-600/40 dark:hover:bg-arena-700/60
                                         text-[12px] font-bold
                                         transition active:scale-95 disabled:opacity-50">
                        <Icon name="task_alt" size={13} /> Mark resolved
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {resolveTarget && (
        <ResolveModal row={resolveTarget}
                      busy={busyId === resolveTarget.id}
                      onCancel={() => setResolveTarget(null)}
                      onConfirm={(note) => resolve(resolveTarget.id, note)} />
      )}

      {/* Batch 2 #24: Retry confirmation. Single click used to re-enqueue
          a settlement immediately — this modal forces an explicit confirm
          so misclicks don't double-fire. */}
      {retryTarget && (
        <DangerConfirmModal
          open={true}
          onClose={() => setRetryTarget(null)}
          onConfirm={async () => {
            await retry(retryTarget.id);
            setRetryTarget(null);
          }}
          title={`Re-enqueue settlement for ${retryTarget.match_id}?`}
          description={
            <>
              The job will be pushed back into <code className="font-mono">pending_settlements</code> and
              the settlement worker will re-attempt it on its next tick. Idempotency keys protect
              against double-credit on the wallet side, but this WILL emit fresh events. Don't fire
              this twice in quick succession.
            </>
          }
          confirmLabel="Re-enqueue"
          cancelLabel="Cancel"
        />
      )}
    </div>
  );
}

function ResolveModal({
  row, busy, onCancel, onConfirm,
}: {
  row: DlqRow; busy: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const can = note.trim().length > 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-start sm:place-items-center p-4 overflow-y-auto"
         onClick={onCancel}>
      <motion.div onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, scale: 0.95, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="rounded-2xl max-w-md w-full p-5 space-y-4
                             bg-white dark:bg-arena-800
                             ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                             shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                           bg-amber-100 text-amber-700 ring-1 ring-amber-200
                           dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
            <Icon name="task_alt" size={20} filled />
          </span>
          <div>
            <h3 className="text-[15px] font-bold text-arena-900 dark:text-white">Mark resolved</h3>
            <p className="text-[12px] text-arena-600 dark:text-neon-200/60 mt-0.5">
              Match <span className="font-mono">{row.match_id}</span> · {row.attempts} failed attempt{row.attempts === 1 ? '' : 's'}.
              This removes the row from the DLQ but does NOT settle the match.
            </p>
          </div>
        </div>

        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 mb-1 font-semibold">Resolution note (required)</span>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Settled manually via admin tools after fix-up; safe to dismiss."
                    className="w-full rounded-xl px-3 py-2 text-[13px]
                               bg-white dark:bg-arena-800/60
                               ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                               text-arena-900 dark:text-white
                               placeholder-arena-400 dark:placeholder-neon-200/40
                               outline-none focus:ring-violet-400 dark:focus:ring-violet-300 focus:ring-2 resize-y" />
        </label>

        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={busy}
                  className="px-4 py-2 rounded-full text-[12px] font-bold
                             bg-white text-arena-700 dark:text-neon-200/80 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50
                             dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60
                             disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => onConfirm(note.trim())} disabled={!can || busy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                             bg-amber-600 hover:bg-amber-500 active:bg-amber-700
                             text-white text-[12px] font-bold
                             shadow-[0_2px_10px_rgba(245,158,11,0.35)]
                             transition active:scale-95 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="task_alt" size={13} />}
            Confirm resolve
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}
